<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { isPhoneE164 } from 'shared/helpers/Validators';
import ConversationAPI from 'dashboard/api/conversations';
import Popover from 'dashboard/components-next/popover/Popover.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const { t } = useI18n();
const phoneNumber = ref('');
const selectedInboxId = ref('');
const message = ref('');
const isCreating = ref(false);
const popover = ref(null);

const inboxes = useMapGetter('inboxes/getInboxes');
const currentUser = useMapGetter('getCurrentUser');

const phoneInboxes = computed(() =>
  inboxes.value.filter(inbox =>
    ['Channel::Sms', 'Channel::TwilioSms', 'Channel::Whatsapp'].includes(
      inbox.channel_type
    )
  )
);

const inboxOptions = computed(() =>
  phoneInboxes.value.map(inbox => ({
    value: String(inbox.id),
    label: inbox.name,
  }))
);

const selectedInbox = computed(() =>
  phoneInboxes.value.find(
    inbox => String(inbox.id) === String(selectedInboxId.value)
  )
);

const normalizedPhoneNumber = computed(() => {
  const digits = phoneNumber.value.replace(/\D/g, '');
  return digits ? `+${digits}` : '';
});

const sourceId = computed(() => {
  const number = normalizedPhoneNumber.value;
  if (selectedInbox.value?.channel_type === 'Channel::Whatsapp') {
    return number.replace('+', '');
  }
  if (
    selectedInbox.value?.channel_type === 'Channel::TwilioSms' &&
    selectedInbox.value?.medium === 'whatsapp'
  ) {
    return `whatsapp:${number}`;
  }
  return number;
});

const reset = () => {
  phoneNumber.value = '';
  selectedInboxId.value = '';
  message.value = '';
};

const createConversation = async () => {
  if (
    !isPhoneE164(normalizedPhoneNumber.value) ||
    !selectedInbox.value ||
    !message.value.trim()
  )
    return;
  isCreating.value = true;
  try {
    await ConversationAPI.create({
      phone_number: normalizedPhoneNumber.value,
      inbox_id: selectedInbox.value.id,
      source_id: sourceId.value,
      assignee_id: currentUser.value.id,
      message: { content: message.value.trim() },
    });
    useAlert(t('CHAT_LIST.QUICK_CONVERSATION_SUCCESS'));
    reset();
    popover.value?.hide();
  } catch (error) {
    useAlert(t('CHAT_LIST.QUICK_CONVERSATION_ERROR'));
  } finally {
    isCreating.value = false;
  }
};
</script>

<template>
  <Popover ref="popover" align="start" :show-content-border="false">
    <template #default="{ isOpen }">
      <slot name="trigger" :is-open="isOpen" />
    </template>
    <template #content>
      <form class="w-64 p-3 space-y-3" @submit.prevent="createConversation">
        <div class="flex items-center justify-between">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ $t('CHAT_LIST.QUICK_CONVERSATION_TITLE') }}
          </h2>
          <NextButton
            type="button"
            variant="ghost"
            icon="i-lucide-x"
            slate
            xs
            @click="popover.hide()"
          />
        </div>
        <Input
          v-model="phoneNumber"
          type="tel"
          inputmode="tel"
          size="md"
          autofocus
          :placeholder="$t('CHAT_LIST.QUICK_CONVERSATION_PHONE_PLACEHOLDER')"
        />
        <Select
          v-model="selectedInboxId"
          class="w-full"
          :options="inboxOptions"
          :placeholder="$t('CHAT_LIST.QUICK_CONVERSATION_INBOX_PLACEHOLDER')"
          :aria-label="$t('CHAT_LIST.QUICK_CONVERSATION_INBOX_PLACEHOLDER')"
        />
        <TextArea
          v-model="message"
          rows="3"
          :placeholder="$t('CHAT_LIST.QUICK_CONVERSATION_MESSAGE_PLACEHOLDER')"
          class="w-full"
        />
        <NextButton
          type="submit"
          color="blue"
          class="w-full justify-center"
          :is-loading="isCreating"
          :disabled="
            !isPhoneE164(normalizedPhoneNumber) ||
            !selectedInbox ||
            !message.trim()
          "
        >
          {{ $t('CHAT_LIST.QUICK_CONVERSATION_START') }}
        </NextButton>
      </form>
    </template>
  </Popover>
</template>
